import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";

interface ActivityLog {
  id: string;
  tenant_id: string;
  user_id: string | null;
  action: string;
  entity: string;
  entity_id: string;
  entity_name: string | null;
  created_at: string;
}

interface Props {
  contractId: string;
  clientId: string;
  tenantId: string;
}

/* ─── Action metadata ─── */
const ACTION_META: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  created:  { label: "Created",     icon: "ri-add-circle-line",      color: "text-emerald-600", bg: "bg-emerald-50" },
  updated:  { label: "Updated",     icon: "ri-pencil-line",           color: "text-[#C8A96E]",   bg: "bg-[#C8A96E]/10" },
  deleted:  { label: "Deleted",     icon: "ri-delete-bin-line",       color: "text-red-500",     bg: "bg-red-50" },
  signed:   { label: "Signed",      icon: "ri-pen-nib-fill",          color: "text-emerald-600", bg: "bg-emerald-50" },
  voided:   { label: "Voided",      icon: "ri-close-circle-line",     color: "text-red-500",     bg: "bg-red-50" },
  paid:     { label: "Payment",     icon: "ri-money-dollar-circle-line", color: "text-emerald-600", bg: "bg-emerald-50" },
  sent:     { label: "Sent",        icon: "ri-send-plane-line",       color: "text-sky-600",     bg: "bg-sky-50" },
  archived: { label: "Archived",    icon: "ri-archive-line",          color: "text-slate-500",   bg: "bg-slate-100" },
  reset:    { label: "Reset",       icon: "ri-refresh-line",          color: "text-amber-600",   bg: "bg-amber-50" },
  activated:{ label: "Activated",   icon: "ri-checkbox-circle-line",  color: "text-emerald-600", bg: "bg-emerald-50" },
  disabled: { label: "Disabled",    icon: "ri-eye-off-line",          color: "text-slate-500",   bg: "bg-slate-100" },
};

const getActionMeta = (action: string) =>
  ACTION_META[action] ?? { label: action, icon: "ri-information-line", color: "text-slate-400", bg: "bg-slate-100" };

/* ─── Entity label ─── */
const ENTITY_LABEL: Record<string, string> = {
  contract: "Contract",
  payment:  "Payment",
  addon:    "Add-on",
  job:      "Job",
  client:   "Client",
  message:  "Message",
  file:     "File",
};

const getEntityLabel = (entity: string) => ENTITY_LABEL[entity] ?? entity;

export default function ActivityTab({ contractId, clientId, tenantId }: Props) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "contract" | "payment" | "addon">("all");

  const fetchLogs = useCallback(async () => {
    setLoading(true);

    // Fetch logs for:
    // 1. This contract specifically
    // 2. Payments linked to this contract (entity_name contains contract_number)
    // 3. Addons with entity_id = contractId
    const { data, error } = await supabase
      .from("activity_logs")
      .select("*")
      .eq("tenant_id", tenantId)
      .or(`entity_id.eq.${contractId},entity_id.eq.${clientId}`)
      .in("entity", ["contract", "payment", "addon", "client"])
      .order("created_at", { ascending: false })
      .limit(100);

    if (!error && data) {
      // Keep contract-related entries and filter out unrelated client entries
      const filtered = (data as ActivityLog[]).filter((log) => {
        if (log.entity === "contract" && log.entity_id === contractId) return true;
        if (log.entity === "payment") return true;
        if (log.entity === "addon") return true;
        if (log.entity === "client" && log.entity_id === clientId) return true;
        return false;
      });
      setLogs(filtered);
    }
    setLoading(false);
  }, [contractId, clientId, tenantId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filtered = filter === "all"
    ? logs
    : logs.filter((l) => l.entity === filter);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatFullDate = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

  /* Group logs by day */
  const grouped = filtered.reduce<{ date: string; items: ActivityLog[] }[]>((acc, log) => {
    const day = new Date(log.created_at).toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
    const existing = acc.find((g) => g.date === day);
    if (existing) {
      existing.items.push(log);
    } else {
      acc.push({ date: day, items: [log] });
    }
    return acc;
  }, []);

  return (
    <div className="space-y-6">
      {/* Header + filters */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center bg-slate-100 rounded-lg">
            <i className="ri-history-line text-slate-500 text-sm"></i>
          </div>
          <div>
            <p className="text-[#0f172a] font-semibold text-sm">Activity Log</p>
            <p className="text-slate-400 text-xs">{logs.length} events recorded</p>
          </div>
        </div>

        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
          {(["all", "contract", "payment", "addon"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                filter === f
                  ? "bg-[#C8A96E] text-white"
                  : "text-slate-400 hover:text-[#0f172a]"
              }`}
            >
              {f === "all" ? "All" : getEntityLabel(f)}
            </button>
          ))}
        </div>

        <button
          onClick={fetchLogs}
          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          title="Refresh"
        >
          <i className="ri-refresh-line text-sm"></i>
        </button>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-slate-100 animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-1.5 py-1">
                <div className="h-3.5 bg-slate-100 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-slate-100 rounded animate-pulse w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-100">
            <i className="ri-history-line text-slate-300 text-2xl"></i>
          </div>
          <div className="text-center">
            <p className="text-slate-600 font-medium text-sm mb-1">No activity yet</p>
            <p className="text-slate-400 text-xs">
              {filter !== "all"
                ? `No ${getEntityLabel(filter).toLowerCase()} events recorded.`
                : "Activity will appear here as you work with this contract."}
            </p>
          </div>
          {filter !== "all" && (
            <button
              onClick={() => setFilter("all")}
              className="text-xs text-[#C8A96E] hover:underline cursor-pointer"
            >
              Show all events →
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(({ date, items }) => (
            <div key={date}>
              {/* Date divider */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">
                  {date}
                </span>
                <div className="h-px flex-1 bg-slate-100" />
              </div>

              {/* Events for this day */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
                {items.map((log, idx) => {
                  const meta = getActionMeta(log.action);
                  return (
                    <div key={log.id} className="flex items-start gap-3.5 px-4 py-3.5 hover:bg-slate-50/60 transition-colors group">
                      {/* Icon */}
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 ${meta.bg}`}>
                        <i className={`${meta.icon} ${meta.color} text-sm`}></i>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Action badge */}
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>
                            {meta.label}
                          </span>
                          {/* Entity badge */}
                          <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-medium">
                            {getEntityLabel(log.entity)}
                          </span>
                        </div>

                        {log.entity_name && (
                          <p className="text-slate-700 text-xs mt-0.5 leading-relaxed truncate max-w-lg">
                            {log.entity_name}
                          </p>
                        )}
                      </div>

                      {/* Timestamp */}
                      <div className="flex-shrink-0 text-right">
                        <p
                          className="text-slate-400 text-xs"
                          title={formatFullDate(log.created_at)}
                        >
                          {formatDate(log.created_at)}
                        </p>
                        {idx === 0 && (
                          <span className="text-[10px] text-slate-300 hidden group-hover:inline">
                            {formatFullDate(log.created_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats footer */}
      {logs.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            {
              label: "Contract events",
              count: logs.filter((l) => l.entity === "contract").length,
              icon: "ri-file-text-line",
              color: "text-[#C8A96E]",
            },
            {
              label: "Payment events",
              count: logs.filter((l) => l.entity === "payment").length,
              icon: "ri-money-dollar-circle-line",
              color: "text-emerald-600",
            },
            {
              label: "Add-on events",
              count: logs.filter((l) => l.entity === "addon").length,
              icon: "ri-puzzle-line",
              color: "text-violet-500",
            },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-50 flex-shrink-0">
                <i className={`${stat.icon} ${stat.color} text-sm`}></i>
              </div>
              <div>
                <p className="text-[#0f172a] font-bold text-lg leading-none">{stat.count}</p>
                <p className="text-slate-400 text-[10px] mt-0.5">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}